"use client";

import type React from "react";
import {
  createContext,
  use,
  useMemo,
  useRef,
  useState,
  useCallback,
  useEffect,
} from "react";

/**
 * Context for synchronizing hover state between list view cards and map markers.
 * Enables bidirectional highlighting when hovering over cards or markers.
 */
type MapListSyncContextType = {
  /** The ID of the currently hovered item (club/event), or null if none */
  hoveredItemId: string | null;
  /** Function to update the hovered item ID */
  setHoveredItemId: (id: string | null) => void;
};

const MapListSyncContext = createContext<MapListSyncContextType | undefined>(
  undefined,
);

/**
 * Provider component for map-list synchronization context.
 * Implements debouncing via requestAnimationFrame to prevent excessive re-renders.
 */
export function MapListSyncProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [hoveredItemId, setHoveredItemIdState] = useState<string | null>(null);
  const rafRef = useRef<number | null>(null);
  const pendingIdRef = useRef<string | null>(null);

  /**
   * Debounced setter that batches updates using requestAnimationFrame.
   * This ensures updates happen at most once per frame (16ms at 60fps).
   */
  const setHoveredItemId = useCallback((id: string | null) => {
    // Store the pending ID
    pendingIdRef.current = id;

    // Cancel any existing RAF
    if (rafRef.current !== null) {
      cancelAnimationFrame(rafRef.current);
    }

    // Schedule update for next frame
    rafRef.current = requestAnimationFrame(() => {
      setHoveredItemIdState(pendingIdRef.current);
      rafRef.current = null;
    });
  }, []);

  // Cleanup RAF on unmount
  useEffect(() => {
    return () => {
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current);
      }
    };
  }, []);

  // Memoize context value to prevent unnecessary re-renders
  const value = useMemo(
    () => ({
      hoveredItemId,
      setHoveredItemId,
    }),
    [hoveredItemId, setHoveredItemId],
  );

  return (
    <MapListSyncContext.Provider value={value}>
      {children}
    </MapListSyncContext.Provider>
  );
}

/**
 * Hook to access map-list synchronization context.
 * Must be used within a MapListSyncProvider.
 *
 * @throws {Error} If used outside of MapListSyncProvider
 * @returns {MapListSyncContextType} The context value with hoveredItemId and setter
 *
 * @example
 * ```tsx
 * function ClubCard({ club }) {
 *   const { setHoveredItemId } = useMapListSync();
 *   return (
 *     <div
 *       onMouseEnter={() => setHoveredItemId(club.id)}
 *       onMouseLeave={() => setHoveredItemId(null)}
 *     >
 *       {club.name}
 *     </div>
 *   );
 * }
 * ```
 */
export function useMapListSync() {
  const context = use(MapListSyncContext);
  if (context === undefined) {
    throw new Error("useMapListSync must be used within a MapListSyncProvider");
  }
  return context;
}
